
<template>
  <div class="dashboard-grid">
    <!-- Total Devices -->
    <div class="metric-card">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-blue-100 rounded-md flex items-center justify-center">
            <svg class="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
            </svg>
          </div>
        </div>
        <div class="ml-5 w-0 flex-1">
          <dl>
            <dt class="text-sm font-medium text-gray-500 truncate">Total Devices</dt>
            <dd class="flex items-baseline">
              <div class="text-2xl font-semibold text-gray-900">
                <span v-if="loading" class="animate-pulse">--</span>
                <span v-else>{{ stats.totalDevices }}</span>
              </div>
            </dd>
          </dl>
        </div>
      </div>
    </div>

    <!-- Online Devices -->
    <div class="metric-card">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-green-100 rounded-md flex items-center justify-center">
            <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5.636 18.364a9 9 0 010-12.728m12.728 0a9 9 0 010 12.728m-9.9-2.829a5 5 0 010-7.07m7.072 0a5 5 0 010 7.07M13 12a1 1 0 11-2 0 1 1 0 012 0z" />
            </svg>
          </div>
        </div>
        <div class="ml-5 w-0 flex-1">
          <dl>
            <dt class="text-sm font-medium text-gray-500 truncate">Online</dt>
            <dd class="flex items-baseline">
              <div class="text-2xl font-semibold text-green-600">
                <span v-if="loading" class="animate-pulse">--</span>
                <span v-else>{{ stats.onlineDevices }}</span>
              </div>
            </dd>
          </dl>
        </div>
      </div>
    </div>

    <!-- Offline Devices -->
    <div class="metric-card">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-red-100 rounded-md flex items-center justify-center">
            <svg class="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21.192 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3l8.293 8.293a1 1 0 101.414-1.414L21 21l-8.293-8.293a1 1 0 00-1.414 1.414z" />
            </svg>
          </div>
        </div>
        <div class="ml-5 w-0 flex-1">
          <dl>
            <dt class="text-sm font-medium text-gray-500 truncate">Offline</dt>
            <dd class="flex items-baseline">
              <div class="text-2xl font-semibold text-red-600">
                <span v-if="loading" class="animate-pulse">--</span>
                <span v-else>{{ stats.offlineDevices }}</span>
              </div>
            </dd>
          </dl>
        </div>
      </div>
    </div>

    <!-- Uptime Percentage -->
    <div class="metric-card">
      <div class="flex items-center">
        <div class="flex-shrink-0">
          <div class="w-8 h-8 bg-indigo-100 rounded-md flex items-center justify-center">
            <svg class="w-5 h-5 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          </div>
        </div>
        <div class="ml-5 w-0 flex-1">
          <dl>
            <dt class="text-sm font-medium text-gray-500 truncate">Uptime</dt>
            <dd class="flex items-baseline">
              <div class="text-2xl font-semibold text-gray-900">
                <span v-if="loading" class="animate-pulse">--</span>
                <span v-else>{{ stats.uptimePercentage }}%</span>
              </div>
              <div class="ml-2 flex items-baseline text-sm">
                <span 
                  class="font-medium"
                  :class="uptimeColorClass"
                >
                  {{ uptimeStatus }}
                </span>
              </div>
            </dd>
          </dl>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  stats: {
    type: Object,
    required: true
  },
  loading: {
    type: Boolean,
    default: false
  }
})

const uptimeColorClass = computed(() => {
  const uptime = props.stats.uptimePercentage
  if (uptime >= 95) return 'text-green-600'
  if (uptime >= 80) return 'text-yellow-600' 
  return 'text-red-600'
})

const uptimeStatus = computed(() => {
  const uptime = props.stats.uptimePercentage
  if (uptime >= 95) return 'Excellent'
  if (uptime >= 80) return 'Good'
  if (uptime >= 60) return 'Fair'
  return 'Poor'
})
</script>